import React, { useEffect, useState } from "react";
import { IoOpenOutline } from "react-icons/io5";
import { serverInstance } from "../../service/axios";
import "./CustomPopup.scss";

const CUSTOM_POPUP_ID = "CUSTOM_POPUP";
const ASSET_BASE_URL = "https://msid-asset.s3.ap-northeast-2.amazonaws.com";
const DEFAULT_LINK_LABEL_EN = "Learn more";
const DEFAULT_LINK_LABEL_MN = "Дэлгэрэнгүй";

const getLocalizedField = (item, field, language) => {
  const localizedField = `${field}_en`;
  if (language && item?.[localizedField]) {
    return item[localizedField];
  }
  return item?.[field] || "";
};

const buildImageUrl = (fileUrl, cacheKey) => {
  if (!fileUrl) {
    return "";
  }

  const baseUrl = /^https?:\/\//i.test(fileUrl)
    ? fileUrl
    : `${ASSET_BASE_URL}/${fileUrl}`;

  if (!cacheKey) {
    return baseUrl;
  }

  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}v=${encodeURIComponent(cacheKey)}`;
};

const resolvePopupContent = (item, language) => {
  if (!item || item.active === false) {
    return {
      active: false,
      title: "",
      text: "",
      imageUrl: "",
      link: "",
      buttonText: "",
    };
  }

  const buttonText = getLocalizedField(item, "button_text", language);

  return {
    active: true,
    title: getLocalizedField(item, "title", language),
    text: getLocalizedField(item, "description", language),
    imageUrl: buildImageUrl(item.fileUrl, item.updated_at),
    link: item.link || "",
    buttonText: buttonText || (language ? DEFAULT_LINK_LABEL_EN : DEFAULT_LINK_LABEL_MN),
  };
};

const hasRichTextContent = (html) => {
  if (!html) {
    return false;
  }
  return html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").trim().length > 0;
};

const CustomPopup = ({ language }) => {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [link, setLink] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadPopupContent = async () => {
      setLoading(true);

      try {
        const res = await serverInstance.post("/app/item", { class: "popup", id: CUSTOM_POPUP_ID }, {
          headers: { "Content-Type": "application/json" },
        });

        if (cancelled) {
          return;
        }

        const content = resolvePopupContent(res.data.item, language);
        setActive(content.active);
        setTitle(content.title);
        setText(content.text);
        setImageUrl(content.imageUrl);
        setLink(content.link);
        setButtonText(content.buttonText);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setActive(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPopupContent();

    return () => {
      cancelled = true;
    };
  }, [language]);

  if (loading) {
    return (
      <div className="custom-popup custom-popup--loading custom-popup--with-hero" aria-busy="true" aria-live="polite">
        <div className="custom-popup__hero-skeleton" />
        <div className="custom-popup__content">
          <div className="custom-popup__skeleton custom-popup__skeleton--title" />
          <div className="custom-popup__skeleton custom-popup__skeleton--line" />
          <div className="custom-popup__skeleton custom-popup__skeleton--line custom-popup__skeleton--short" />
        </div>
      </div>
    );
  }

  if (!active) {
    return null;
  }

  const hasText = hasRichTextContent(text);
  if (!title && !hasText && !imageUrl) {
    return null;
  }

  const linkProps = link ? {
    href: link,
    target: "_blank",
    rel: "noopener noreferrer",
  } : null;

  const heroImage = imageUrl ? (
    <img
      className="custom-popup__image"
      src={imageUrl}
      alt={title || ""}
      loading="lazy"
    />
  ) : null;

  return (
    <article className={`custom-popup${imageUrl ? " custom-popup--with-hero" : ""}`} aria-labelledby={title ? "custom-popup-title" : undefined}>
      {imageUrl ?
        <figure className="custom-popup__hero">
          {link ?
            <a className="custom-popup__hero-link" {...linkProps} aria-label={buttonText}>
              {heroImage}
            </a>
          :
            heroImage
          }
        </figure>
      : null}

      <div className={`custom-popup__content${imageUrl ? "" : " custom-popup__content--standalone"}`}>
        {title ?
          <>
            <h2 id="custom-popup-title" className="custom-popup__title">{title}</h2>
            <span className="custom-popup__accent" aria-hidden="true" />
          </>
        : null}

        {hasText ?
          <div
            className="custom-popup__text"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        : null}

        {link ?
          <div className="custom-popup__actions">
            <a className="custom-popup__cta" {...linkProps}>
              <span>{buttonText}</span>
              <IoOpenOutline aria-hidden="true" />
            </a>
          </div>
        : null}
      </div>
    </article>
  );
};

export default CustomPopup;
